'use client';

import React, { useState, useCallback } from 'react';
import { OutfitData } from '../types/game';
import AppImage from '@/components/ui/AppImage';

interface OutfitUnlockModalProps {
  outfit: OutfitData;
  onClose: () => void;
  onPurchaseSuccess: (outfitId: string) => void;
}

type PurchaseState = 'idle' | 'loading' | 'redirecting' | 'success' | 'error';

export default function OutfitUnlockModal({
  outfit,
  onClose,
  onPurchaseSuccess,
}: OutfitUnlockModalProps) {
  const [purchaseState, setPurchaseState] = useState<PurchaseState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handlePurchase = useCallback(async () => {
    setPurchaseState('loading');
    setErrorMessage('');

    try {
      // Backend integration: POST /api/stripe/create-checkout-session
      // with { priceId: outfit.stripePriceId, outfitId: outfit.id }
      // Returns { sessionId, url }
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: outfit.stripePriceId,
          outfitId: outfit.id,
          successUrl: `${window.location.origin}/run-scene?outfit_unlocked=${outfit.id}`,
          cancelUrl: `${window.location.origin}/run-scene`,
        }),
      });

      if (!response.ok) {
        throw new Error('Checkout session creation failed');
      }

      const { url } = await response.json();
      setPurchaseState('redirecting');

      // Redirect to Stripe hosted checkout
      window.location.href = url;
    } catch {
      // Dev fallback — simulate success when API not yet configured
      if (process.env.NODE_ENV === 'development') {
        setPurchaseState('success');
        setTimeout(() => onPurchaseSuccess(outfit.id), 1200);
        return;
      }
      setPurchaseState('error');
      setErrorMessage('Payment unavailable right now. Please try again shortly.');
    }
  }, [outfit, onPurchaseSuccess]);

  const priceDisplay = `$${(outfit.price / 100).toFixed(2)}`;

  return (
    <div className="absolute inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(13,13,15,0.82)', backdropFilter: 'blur(10px)' }}
        onClick={purchaseState === 'idle' || purchaseState === 'error' ? onClose : undefined}
      />

      {/* Sheet */}
      <div
        className="relative w-full max-w-sm stripe-modal-enter"
        style={{
          background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(13,13,15,0.99) 100%)',
          border: '1px solid rgba(0,229,255,0.2)',
          borderBottom: 'none',
          borderRadius: '28px 28px 0 0',
          padding: '28px 24px 40px',
        }}
      >
        {/* Drag handle */}
        <div
          className="mx-auto mb-5 rounded-full"
          style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)' }}
        />

        {/* Close button */}
        {(purchaseState === 'idle' || purchaseState === 'error') && (
          <button
            className="absolute top-5 right-5 btn-action w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.08)' }}
            onClick={onClose}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8899aa" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}

        {purchaseState === 'success' ? (
          /* Success state */
          <div className="flex flex-col items-center gap-4 py-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,229,255,0.12)', border: '2px solid #00e5ff' }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00e5ff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold" style={{ color: '#00e5ff', fontFamily: 'DM Sans, sans-serif' }}>
                Outfit Unlocked!
              </p>
              <p className="text-sm mt-1" style={{ color: '#8899aa', fontFamily: 'DM Sans, sans-serif' }}>
                {outfit.name} is now in your wardrobe.
              </p>
            </div>
          </div>
        ) : purchaseState === 'redirecting' ? (
          /* Redirecting state */
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="w-10 h-10 border-3 border-[#00e5ff] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-medium" style={{ color: '#00e5ff', fontFamily: 'DM Sans, sans-serif' }}>
              Opening secure checkout…
            </p>
            <p className="text-xs text-center" style={{ color: '#556677', fontFamily: 'DM Sans, sans-serif' }}>
              You&apos;ll be redirected to Stripe. Your run will resume after payment.
            </p>
          </div>
        ) : (
          /* Idle / Error state */
          <>
            {/* Header */}
            <div className="flex flex-col gap-1 mb-5">
              <div className="flex items-center gap-2">
                <span
                  className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(255,214,10,0.12)',
                    color: '#ffd60a',
                    border: '1px solid rgba(255,214,10,0.25)',
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  New Outfit
                </span>
                <span
                  className="text-[10px] font-medium tracking-widest uppercase"
                  style={{ color: '#556677', fontFamily: 'DM Sans, sans-serif' }}
                >
                  Unlock to Wear
                </span>
              </div>
              <h2
                className="text-2xl font-bold mt-1"
                style={{ color: '#ffffff', fontFamily: 'DM Sans, sans-serif' }}
              >
                {outfit.name}
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: '#8899aa', fontFamily: 'DM Sans, sans-serif' }}>
                {outfit.description}
              </p>
            </div>

            {/* Outfit preview */}
            <div
              className="w-full rounded-2xl overflow-hidden mb-5 flex items-center justify-center"
              style={{
                height: '180px',
                background: 'linear-gradient(135deg, rgba(0,229,255,0.06) 0%, rgba(255,77,109,0.06) 100%)',
                border: '1px solid rgba(0,229,255,0.15)',
              }}
            >
              {outfit.previewImageUrl ? (
                <AppImage
                  src={outfit.previewImageUrl}
                  alt={`${outfit.name} outfit preview — rider wearing ${outfit.name} skateboarding costume`}
                  width={160}
                  height={180}
                  className="object-contain h-full"
                />
              ) : (
                /* Placeholder when image not yet provided */
                <div className="flex flex-col items-center gap-3">
                  <svg width="64" height="80" viewBox="0 0 80 100" fill="none">
                    <circle cx="40" cy="18" r="12" fill="rgba(0,229,255,0.5)" />
                    <path d="M40 6 Q52 10 56 22" stroke="rgba(255,214,10,0.8)" strokeWidth="3" strokeLinecap="round" fill="none" />
                    <rect x="26" y="32" width="28" height="26" rx="5" fill="rgba(0,229,255,0.4)" />
                    <ellipse cx="33" cy="36" rx="7" ry="5" fill="rgba(255,77,109,0.7)" />
                    <ellipse cx="47" cy="36" rx="7" ry="5" fill="rgba(255,77,109,0.7)" />
                    <rect x="26" y="48" width="12" height="22" rx="4" fill="rgba(0,229,255,0.4)" />
                    <rect x="42" y="48" width="12" height="22" rx="4" fill="rgba(0,229,255,0.4)" />
                    <rect x="16" y="70" width="48" height="8" rx="4" fill="rgba(255,77,109,0.6)" />
                    <circle cx="26" cy="80" r="5" fill="rgba(0,229,255,0.8)" />
                    <circle cx="54" cy="80" r="5" fill="rgba(0,229,255,0.8)" />
                  </svg>
                  <p className="text-xs" style={{ color: '#556677', fontFamily: 'DM Sans, sans-serif' }}>
                    Preview coming soon
                  </p>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-5">
              {outfit.tags.map(tag => (
                <span
                  key={`tag-${tag}`}
                  className="text-[10px] font-medium tracking-wide px-2.5 py-1 rounded-full"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#8899aa',
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Error message */}
            {purchaseState === 'error' && errorMessage && (
              <div
                className="w-full rounded-xl px-4 py-3 mb-4 flex items-start gap-3"
                style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid rgba(255,77,109,0.3)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff4d6d" strokeWidth="2" strokeLinecap="round" className="flex-shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p className="text-xs leading-relaxed" style={{ color: '#ff4d6d', fontFamily: 'DM Sans, sans-serif' }}>
                  {errorMessage}
                </p>
              </div>
            )}

            {/* Purchase CTA */}
            <button
              className="w-full py-4 rounded-2xl font-bold text-base tracking-wide btn-action flex items-center justify-center gap-3"
              style={{
                background:
                  purchaseState === 'loading' ?'rgba(0,229,255,0.2)' :'linear-gradient(135deg, #00e5ff 0%, #0077ff 100%)',
                color: purchaseState === 'loading' ? '#00e5ff' : '#0d0d0f',
                fontFamily: 'DM Sans, sans-serif',
                opacity: purchaseState === 'loading' ? 0.8 : 1,
              }}
              onClick={handlePurchase}
              onTouchStart={e => { e.preventDefault(); if (purchaseState === 'idle' || purchaseState === 'error') handlePurchase(); }}
              disabled={purchaseState === 'loading' || purchaseState === 'redirecting'}
            >
              {purchaseState === 'loading' ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#00e5ff] border-t-transparent rounded-full animate-spin" />
                  <span>Processing…</span>
                </>
              ) : (
                <>
                  {/* Stripe lock icon */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span>Unlock for {priceDisplay}</span>
                </>
              )}
            </button>

            {/* Stripe trust badge */}
            <div className="flex items-center justify-center gap-2 mt-3">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#556677" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <p className="text-[10px]" style={{ color: '#556677', fontFamily: 'DM Sans, sans-serif' }}>
                Secure payment via Stripe · One-time unlock · No subscription
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}